import { useState, useMemo, useCallback } from "react";

const ROOT_BREADCRUMB = { id: null, name: "Root" };

export const useFolderNavigation = (fetchCallback, rootName = "Root") => {
  const [folderStack, setFolderStackState] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderLoading, setFolderLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const setFolderStack = useCallback((stackOrFn) => {
    setFolderStackState((prev) => {
      const nextStack = typeof stackOrFn === "function" ? stackOrFn(prev) : stackOrFn;
      if (!Array.isArray(nextStack)) return prev;
      
      return nextStack.filter((item) => item.id !== null);
    });
  }, []);

  const breadcrumbTrail = useMemo(() => {
    return [{ id: null, name: rootName }, ...folderStack];
  }, [folderStack, rootName]);

  const executeWithLoader = useCallback(async (action) => {
    if (folderLoading) return;
    setFolderLoading(true);
    try {
      await action();
    } catch (error) {
      console.error("Navigation action failed: ", error);
    } finally {
      setTimeout(() => {
        setFolderLoading(false);
      }, 500);
    }
  }, [folderLoading]);

  const navigateToBreadcrumb = useCallback((index, courseId) => {
    executeWithLoader(async () => {
      setSearchText("");
      if (index === 0) {
        setCurrentFolderId(null);
        setFolderStack([]);
        await fetchCallback(null, courseId, "");
        return;
      }

      const folderIndex = index - 1;
      const newStack = folderStack.slice(0, folderIndex + 1);
      setFolderStack(newStack);

      const parentId = newStack[folderIndex]?.id ?? null;
      setCurrentFolderId(parentId);
      await fetchCallback(parentId, courseId, "");
    });
  }, [folderStack, fetchCallback, executeWithLoader]);

  const resetFolderNavigation = useCallback((courseId) => {
    executeWithLoader(async () => {
      setCurrentFolderId(null);
      setFolderStack([]);
      setSearchText("");
      await fetchCallback(null, courseId, "");
    });
  }, [fetchCallback, executeWithLoader]);

  const getSearchBreadcrumbs = (item) => {
      const breadcrumbs = [];
    
      const addParents = (current) => {
        if (!current || !current?.parent) return;
    
        if (current?.parent?.parent) {
          addParents(current?.parent);
        }
    
        if (current?.name) {
          breadcrumbs?.push({
            id: current?.id,
            name: current?.name,
          });
        }
      };
    
      addParents(item?.parent);
    
      breadcrumbs?.push({
        id: item?.id,
        name: item?.name,
      });
    
      return breadcrumbs;
  };

  const openFolder = useCallback((doc, courseId) => {
    if(searchText){
      executeWithLoader(async () => {
      setSearchText("");
      setCurrentFolderId(doc?.id);
      setFolderStack((prev) => {
        const lastFolder = prev[prev?.length - 1];
        if (lastFolder?.id === doc?.id) return prev;
  
        let searchBreadcrumbs = getSearchBreadcrumbs(doc)
        return searchBreadcrumbs;
      });
      await fetchCallback(doc.id, courseId, "");
    });
    } else {
      executeWithLoader(async () => {
      setSearchText("");
      setCurrentFolderId(doc.id);
      setFolderStack((prev) => {
        const lastFolder = prev[prev.length - 1];
        if (lastFolder?.id === doc.id) return prev;
        return [...prev, { id: doc.id, name: doc.name }];
      });
      await fetchCallback(doc.id, courseId, "");
    });
    }
  }, [fetchCallback, executeWithLoader]);

  const handleSearch = useCallback((value, courseId) => {
    setSearchText(value);
    fetchCallback(currentFolderId, courseId, value);
  }, [currentFolderId, fetchCallback]);

  return {
    folderStack,
    currentFolderId,
    setCurrentFolderId,
    setFolderStack,
    folderLoading,
    setFolderLoading,
    searchText,
    setSearchText,
    breadcrumbTrail,
    navigateToBreadcrumb,
    resetFolderNavigation,
    openFolder,
    handleSearch,
  };
};